import { CreateBulkAssignGoalTemplateJobInput, GoalConfigurationTemplateTableItemFragment } from "apps/web/src/types";
import { z } from "zod";
import { IntegerSchema } from "@notemeal/validators";
import { ORDERED_DAYS_OF_WEEK } from "@notemeal/utils-date-time";
import { GoalConfigurationDeepPartial, GoalConfigurationFormType } from "../Athlete/GoalTracker/GoalConfiguration/GoalConfigurationSchema";
import { AssignGoalAdvancedSelectionState } from "./useAdvancedSelection";
import { serializeDate } from "@notemeal/utils-date-time";
import { buildAdvancedSelectionInput } from "../Tags/reducers/advancedSelectionReducers";

export enum CreateGoalConfigurationTemplateDialogMode {
    Create = "create",
    Edit = "edit",
    Duplicate = "duplicate",
    View = "view"
}

// Only report "Required" for a genuinely missing value -- for a provided-but-invalid
// value, fall through (return undefined) to zod's own "invalid type" message instead
// of the misleading "Required".
const requiredError = (issue: { input?: unknown }): string | undefined =>
    issue.input === undefined ? "Required" : undefined;

export const GoalCategorySchema = z.object(
    {
        id: z.string().min(1),
        name: z.string().min(1),
        defaultEmoji: z.string().min(1),
    },
    { error: requiredError }
);

export const CompletionCriteriaSchema = z.object({
    markAsComplete: z.boolean({ error: "Please select one of the choices below" }),
    target: z.object({ value: IntegerSchema, unit: z.string({ error: "Required" }).min(1, { message: "Required" }) }).optional(),
});

export const GoalConfigurationTemplateFormSchema = z.object({
    task: z.string({ error: "Task is required" }).min(1, { message: "Required" }).max(40, { message: "Max 40 characters" }),
    type: GoalCategorySchema,
    completionCriteria: CompletionCriteriaSchema,
});

export type GoalConfigurationTemplateFormType = z.infer<typeof GoalConfigurationTemplateFormSchema>;

export const goalConfigurationFormDefaultValues = (goalConfiguration: GoalConfigurationTemplateTableItemFragment | null) => {
    if (goalConfiguration) {
        const { category, target } = goalConfiguration;
        return {
            task: goalConfiguration.name,
            type: {
                id: category.id,
                name: category.name,
                defaultEmoji: category.defaultEmoji,
            },
            completionCriteria: {
                markAsComplete: !target,
                target: target ? { value: target.value, unit: target.unit } : undefined,
            }
        };
    }
    return {
        task: "",
        type: undefined,
        completionCriteria: { markAsComplete: undefined },
    };
};

export const assignGoalConfigurationTemplateFormDefaultValues = (goalConfiguration: GoalConfigurationTemplateTableItemFragment): GoalConfigurationDeepPartial => {
    const { name, category, target } = goalConfiguration;
    const today = new Date();
    return {
        task: name,
        type: {
            id: category.id,
            name: category.name,
            defaultEmoji: category.defaultEmoji,
        },
        completionCriteria: {
            markAsComplete: !target,
            target: target ? { value: target.value, unit: target.unit } : undefined,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        assignments: { type: "weekly", daysOfWeek: [...ORDERED_DAYS_OF_WEEK], startDate: today, endDate: undefined },
        reminders: [],
    };
};

export const createAndAssignGoalConfigurationInput = (goal: GoalConfigurationFormType, goalConfigTemplate: GoalConfigurationTemplateTableItemFragment, advancedSelectState: AssignGoalAdvancedSelectionState): CreateBulkAssignGoalTemplateJobInput => {
    const teamIds = advancedSelectState.__typename === "Teams" ? advancedSelectState.teams.map(({ id }) => id) : null;
    const advancedSelectionInput = advancedSelectState.__typename === "Tags" ? buildAdvancedSelectionInput(advancedSelectState.advancedSelectionState) : null;

    return {
        goalConfigurationTemplateId: goalConfigTemplate.id,
        goalConfigurationTemplateName: goalConfigTemplate.name,
        timezone: goal.timezone,
        advancedSelectionInput,
        teamIds,
        reminders: goal.reminders,
        dateAssignment: {
            individualDates: goal.assignments.type === "individualDates" ? goal.assignments.dates : null,
            daysOfWeek: goal.assignments.type === "weekly" ? goal.assignments.daysOfWeek : null,
            startDate: goal.assignments.type === "weekly" ? serializeDate(goal.assignments.startDate) : null,
            endDate: goal.assignments.type === "weekly" ? serializeDate(goal.assignments.endDate) : null,
        },
    }
}

export const buildEditGoalInput = (
    goal: GoalConfigurationTemplateFormType,
    selectedGoal: GoalConfigurationTemplateTableItemFragment
) => {
    return {
        id: selectedGoal.id,
        name: goal.task,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        targetId: selectedGoal?.target?.id || null,
        target: goal.completionCriteria.markAsComplete
            ? null
            : {
                value: goal.completionCriteria.target?.value || 0,
                unit: goal.completionCriteria.target?.unit || ''
            }
    };
};

export const buildCreateGoalInput = (goal: GoalConfigurationTemplateFormType) => {
    return {
        name: goal.task,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        target: goal.completionCriteria.markAsComplete
            ? null
            : {
                value: goal.completionCriteria.target?.value || 0,
                unit: goal.completionCriteria.target?.unit || ''
            }
    };
};
